import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map, of, tap } from 'rxjs';
import { ApiResponse } from '../../api-response';
import { BaseApiService } from '../../../core/base-api.service';
import { Provider, UpdateProviderRequest } from '../models/provider.model';

@Injectable({ providedIn: 'root' })
export class ProviderService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${this.coreApiUrl}/api/v1/admin/providers`;
  /** Provider type by id, filled by every list/get. Type is immutable, so
   *  entries never go stale (a deleted provider's entry is simply unused). */
  private readonly typeById = new Map<string, string>();

  getAll(page = 0, size = 20): Observable<ApiResponse<Provider[]>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http
      .get<ApiResponse<Provider[]>>(this.baseUrl, { params })
      .pipe(tap((res) => res.data.forEach((p) => this.typeById.set(p.id, p.type))));
  }

  get(id: string): Observable<ApiResponse<Provider>> {
    return this.http
      .get<ApiResponse<Provider>>(`${this.baseUrl}/${id}`)
      .pipe(tap((res) => this.typeById.set(res.data.id, res.data.type)));
  }

  /** A provider's type, from cache when possible — used by the route guards
   *  that pick a provider's route tree. */
  getType(id: string): Observable<string> {
    const cached = this.typeById.get(id);
    return cached ? of(cached) : this.get(id).pipe(map((res) => res.data.type));
  }

  /** Providers register themselves through core's discovery, so there's no create; an update
   *  only enables or disables one. */
  update(id: string, req: UpdateProviderRequest): Observable<ApiResponse<Provider>> {
    return this.http.put<ApiResponse<Provider>>(`${this.baseUrl}/${id}`, req);
  }

  delete(id: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }
}
